/**
 * /algorithm-correctness — public verification page.
 *
 * Marketing claim: "Our algorithms are proven by mathematical tests, not
 * marketing copy." This page is the proof — it hits the live public manifest
 * endpoint /api/algorithm-correctness/manifest, lists every test class +
 * method, and shows the exact curl command to verify the data yourself.
 *
 * Strictly dark theme + emerald-only branding (Feb 27 2026 brand audit).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Copy, Check, ExternalLink, FileCode2, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import SEOHead from '../components/SEOHead';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlgorithmCorrectness = () => {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCmd, setCopiedCmd] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/algorithm-correctness/manifest`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setManifest(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // v34.39 (Feb 2026) — Always display the cartieai.com host in the
  // public curl command, regardless of which preview/staging env this
  // page is rendered from. The "Try the live API" link still uses the
  // real REACT_APP_BACKEND_URL because that's the working endpoint;
  // only the displayed shell command shows the production hostname.
  const PUBLIC_HOST = 'https://cartieai.com';
  const curlCmd = `curl ${PUBLIC_HOST}/api/algorithm-correctness/manifest | jq`;

  const copy = () => {
    navigator.clipboard.writeText(curlCmd);
    setCopiedCmd(true);
    toast.success('curl copied — paste into a terminal to verify yourself');
    setTimeout(() => setCopiedCmd(false), 2000);
  };

  const totalTests = manifest?.total_tests ?? 0;
  const engines = manifest?.engines ?? [];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="algorithm-correctness-page">
      <SEOHead
        title="Algorithm-Correctness Manifest | CARTIEAI"
        description="Every CARTIE algorithm proven by mathematical tests — not marketing claims. Browse the live test manifest or curl it yourself."
      />
      <PublicNavbar />

      <section className="relative px-6 pt-24 pb-12 max-w-5xl mx-auto">
        {/* Soft emerald orbs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
          <div className="absolute top-32 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-emerald-300 mb-6 text-[11px] font-mono uppercase tracking-[0.18em] border border-emerald-500/30">
            <ShieldCheck className="w-3.5 h-3.5" />
            Public Manifest · Mathematical Proof
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight leading-[1.05] text-white">
            Our algorithms are proven by{' '}
            <span className="text-emerald-400">mathematical tests</span> —
            <br />not marketing copy.
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
            Every CARTIE engine ships with a test manifest you can audit. Live count below. No NDA, no "contact sales" — just <code className="text-emerald-300 font-mono bg-slate-900 px-1.5 py-0.5 rounded">curl</code> away.
          </p>
        </div>

        {/* Headline number */}
        <div className="mb-10 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 text-center" data-testid="ac-headline-card">
          {loading ? (
            <div className="flex items-center justify-center gap-3 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin text-emerald-400" />
              Loading live manifest…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center gap-3 text-amber-300">
              <AlertCircle className="w-5 h-5" />
              Could not reach the manifest — {error}.{' '}
              <button onClick={load} className="underline hover:text-amber-200">Retry</button>
            </div>
          ) : (
            <>
              <div className="text-6xl sm:text-7xl font-extrabold text-emerald-400 tabular-nums" data-testid="ac-total">{totalTests}</div>
              <p className="text-base text-slate-300 mt-2 font-medium">
                mathematical tests across <span className="text-white">{engines.length} engines</span>
              </p>
              <p className="text-[11px] text-slate-500 mt-3 font-mono uppercase tracking-widest">
                LIVE · parsed from <span className="text-emerald-300">{manifest?.source_file}</span> · refreshed on each request
              </p>
            </>
          )}
        </div>

        {/* Verify-yourself curl */}
        <div className="mb-10 rounded-xl bg-slate-900/80 border border-white/10 p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase font-mono">Verify yourself · public, no auth</div>
            <button
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-200 hover:text-white text-xs font-semibold transition-all"
              data-testid="ac-copy-curl"
            >
              {copiedCmd ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copiedCmd ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre className="text-sm text-emerald-300 font-mono overflow-x-auto"><code>$ {curlCmd}</code></pre>
        </div>

        {/* Engines breakdown */}
        {!loading && !error && engines.length > 0 && (
          <div className="space-y-5" data-testid="ac-engines">
            <h2 className="text-2xl font-bold text-white tracking-tight">Engines under proof</h2>
            {engines.map((e) => (
              <div key={e.engine} className="rounded-2xl bg-slate-900/60 border border-white/10 overflow-hidden" data-testid={`ac-engine-${e.engine}`}>
                <div className="px-6 py-4 border-b border-white/10 flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-3">
                    <div className="inline-flex w-9 h-9 rounded-lg bg-emerald-500 items-center justify-center">
                      <FileCode2 className="w-4 h-4 text-slate-950" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">{e.label}</h3>
                      <p className="text-[11px] font-mono text-slate-500">{e.test_class}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-0.5">
                      <span className="text-emerald-400 font-bold">{e.test_count}</span> tests
                    </span>
                    {e.page_path && (
                      <Link to={e.page_path} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 font-semibold transition-colors" data-testid={`ac-link-${e.engine}`}>
                        See engine <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
                {Array.isArray(e.test_methods) && e.test_methods.length > 0 && (
                  <ul className="divide-y divide-white/5">
                    {e.test_methods.map((m, i) => (
                      <li key={i} className="px-6 py-2.5 flex items-center gap-3 text-sm hover:bg-white/[0.02] transition-colors">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                        <code className="font-mono text-slate-300 truncate">{m}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer note */}
        {!loading && !error && manifest?.note && (
          <p className="mt-8 text-xs text-slate-500 leading-relaxed text-center max-w-2xl mx-auto italic">
            {manifest.note}
          </p>
        )}

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
          <Link
            to="/honest-scorecard"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors"
            data-testid="ac-cta-scorecard"
          >
            See the head-to-head scorecard <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium text-sm transition-colors"
            data-testid="ac-cta-pricing"
          >
            Start a 14-day pilot
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AlgorithmCorrectness;
